import React, { useState } from 'react';
import { signupOrganizer } from '../services/api';

// Organizer self-signup page. Reached via a "Run your own pool" link
// shared by the platform (e.g. from the Login page or a marketing page).
//
// Flow:
//   1. User fills in name, email, password
//   2. POST /auth/signup-organizer → backend sends verification email
//   3. Show "check your email" confirmation
//
// The backend returns the same generic message whether or not the email
// is already registered, so this page can't be used to discover accounts.

function SignupOrganizer({ onBackToLogin }) {
    const [name,     setName]     = useState('');
    const [email,    setEmail]    = useState('');
    const [password, setPassword] = useState('');
    const [error,    setError]    = useState('');
    const [loading,  setLoading]  = useState(false);
    const [done,     setDone]     = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setLoading(true);
        try {
            await signupOrganizer({ name: name.trim(), email: email.trim().toLowerCase(), password });
            setDone(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                        <div className="text-5xl font-black text-white tracking-tight">
                            Hebert<span className="text-orange-500">Hoops</span>
                        </div>
                    </div>
                    <p className="text-gray-400">Run your own bracket pool</p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    {done ? (
                        <div className="space-y-6">
                            <div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg text-sm">
                                <p className="font-bold mb-1">Check your email</p>
                                <p>
                                    We've sent a verification link to <strong>{email}</strong>.
                                    Click the link in that email to activate your organizer account.
                                    The link expires in 24 hours.
                                </p>
                            </div>
                            <button
                                onClick={onBackToLogin}
                                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                Back to sign in
                            </button>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-white font-bold text-xl mb-2">Create organizer account</h2>
                            <p className="text-gray-400 text-sm mb-6">
                                You'll use this account to manage your tournaments, pools, and participants.
                            </p>

                            {error && (
                                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="signup-name" className="block text-sm font-medium text-gray-300 mb-2">
                                        Your name
                                    </label>
                                    <input
                                        id="signup-name"
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="Your name"
                                        autoComplete="name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="signup-email" className="block text-sm font-medium text-gray-300 mb-2">
                                        Email address
                                    </label>
                                    <input
                                        id="signup-email"
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="signup-password" className="block text-sm font-medium text-gray-300 mb-2">
                                        Password
                                    </label>
                                    <input
                                        id="signup-password"
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="At least 8 characters"
                                        autoComplete="new-password"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading || !name.trim() || !email.trim() || !password}
                                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:opacity-50 text-white font-bold py-3 rounded-lg transition-colors"
                                >
                                    {loading ? 'Sending verification email…' : 'Create account'}
                                </button>
                            </form>

                            <div className="text-center mt-6 pt-6 border-t border-gray-800">
                                <button
                                    onClick={onBackToLogin}
                                    className="text-orange-400 hover:text-orange-300 text-sm font-medium"
                                >
                                    Already have an account? Sign in
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SignupOrganizer;